import * as React from 'react';
import { WorkspaceButton, WorkspaceDialog } from '@/components/ui/workspace';
import {
  existingModuleFromError,
  isPendingRenameClash,
  useCreateCategoryMutation,
  useCreateModuleMutation,
  useSetModuleStatusMutation,
  type CreatedModule,
  type LibraryTree,
} from '@/lib/api/requirement-library';
import { findCategoryByName, findModuleByName } from './library-logic';

/**
 * "New module" dialog (T-0141, E11-F02 scenarios 4, 9, 10; T-0152 restore
 * and new category).
 *
 * Reference: `openNewModule()` in `docs/requirement-library/prototype/
 * selection.js`. Asks for a name and a category and nothing else: the
 * number prefix is assigned by the server (amendment 01 Decision 32), so
 * there is no prefix field and the request carries none.
 *
 * A name that already exists is refused before saving, with a link to the
 * existing module. The check here mirrors the server's normalisation; the
 * server's own `module_name_exists` conflict is handled the same way, which
 * also covers two curators creating the same module at once.
 *
 * A retired module keeps its name (rule 1), so that conflict offers the
 * one thing that resolves it: restore the retired module. A name another
 * module is being renamed to is refused with the server's explanation.
 *
 * The category list ends with "New category": choosing it asks for a name
 * and creates the category first, then the module in it.
 */

const FIELD =
  'block h-9 w-full rounded-card border border-ink-200 bg-paper-0 px-3 text-body text-ink-900 placeholder:text-ink-300 hover:border-ink-300 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-accent-amber-600';
const LINK =
  'rounded-chip font-medium underline underline-offset-[3px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600';

/** `<select>` value that stands for "create a category". */
const NEW_CATEGORY = '__new__';

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

export interface NewModuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tree: LibraryTree | undefined;
  /** Category preselected in the list, usually the one being browsed. */
  defaultCategoryId?: string | null;
  onCreated: (module: CreatedModule) => void;
  /** Open a module that already has this name. */
  onOpenExisting: (moduleId: string) => void;
}

export function NewModuleDialog({
  open,
  onOpenChange,
  tree,
  defaultCategoryId,
  onCreated,
  onOpenExisting,
}: NewModuleDialogProps) {
  const categories = React.useMemo(
    () => (tree?.categories ?? []).filter((c) => c.status === 'active'),
    [tree],
  );
  const [name, setName] = React.useState('');
  const [categoryId, setCategoryId] = React.useState('');
  const [newCategoryName, setNewCategoryName] = React.useState('');
  const [categoryError, setCategoryError] = React.useState<string | null>(null);
  const [restoreError, setRestoreError] = React.useState<string | null>(null);
  const create = useCreateModuleMutation();
  const createCategory = useCreateCategoryMutation();
  const restore = useSetModuleStatusMutation();
  const { reset } = create;

  React.useEffect(() => {
    if (!open) return;
    setName('');
    setNewCategoryName('');
    setCategoryError(null);
    setRestoreError(null);
    setCategoryId(
      categories.find((c) => c.id === defaultCategoryId)?.id ?? categories[0]?.id ?? NEW_CATEGORY,
    );
    reset();
    // Only when the dialog opens: typing must not reset the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const trimmed = name.trim();
  const localClash = trimmed ? findModuleByName(tree, trimmed) : null;
  const serverClash = create.isError ? existingModuleFromError(create.error) : null;
  const pendingRenameClash = create.isError && isPendingRenameClash(create.error);
  const clash = localClash
    ? { id: localClash.id, name: localClash.name, retired: localClash.status === 'retired' }
    : serverClash && !pendingRenameClash
      ? { id: serverClash.id, name: serverClash.name, retired: serverClash.status === 'retired' }
      : null;
  const otherError =
    create.isError && !clash
      ? messageOf(create.error, 'The module could not be created.')
      : (categoryError ?? null);

  const addingCategory = categoryId === NEW_CATEGORY;
  const trimmedCategory = newCategoryName.trim();
  const categoryClash =
    addingCategory && trimmedCategory ? findCategoryByName(tree, trimmedCategory) : null;
  const busy = create.isPending || createCategory.isPending || restore.isPending;
  const canCreate =
    trimmed.length > 0 &&
    categoryId !== '' &&
    (!addingCategory || (trimmedCategory.length > 0 && !categoryClash)) &&
    !localClash &&
    !busy;

  const submit = async () => {
    if (!canCreate) return;
    setCategoryError(null);
    let targetCategoryId = categoryId;
    if (addingCategory) {
      try {
        const category = await createCategory.mutateAsync(trimmedCategory);
        targetCategoryId = category.id;
        // The category exists now: a retry of the module must not create it again.
        setCategoryId(category.id);
        setNewCategoryName('');
      } catch (err) {
        setCategoryError(messageOf(err, 'The category could not be created.'));
        return;
      }
    }
    create.mutate(
      { name: trimmed, category_id: targetCategoryId },
      {
        onSuccess: (module) => {
          onOpenChange(false);
          onCreated(module);
        },
      },
    );
  };

  const restoreExisting = (moduleId: string) => {
    setRestoreError(null);
    restore.mutate(
      { moduleId, action: 'restore' },
      {
        onSuccess: () => {
          onOpenChange(false);
          onOpenExisting(moduleId);
        },
        onError: (err) => setRestoreError(messageOf(err, 'The module could not be restored.')),
      },
    );
  };

  // A category created a moment ago may not be in the tree yet.
  const categoryOptions =
    categoryId && categoryId !== NEW_CATEGORY && !categories.some((c) => c.id === categoryId)
      ? [...categories, { id: categoryId, name: trimmedCategory || 'New category' }]
      : categories;

  return (
    <WorkspaceDialog
      open={open}
      onOpenChange={onOpenChange}
      title="New module"
      description="Starts as a private draft. Engagements cannot add it until you publish version 1."
      actions={
        <>
          <WorkspaceButton size="md" onClick={() => onOpenChange(false)}>
            Cancel
          </WorkspaceButton>
          <WorkspaceButton
            size="md"
            variant="primary"
            disabled={!canCreate}
            onClick={() => void submit()}
          >
            {create.isPending || createCategory.isPending
              ? 'Creating…'
              : 'Create and start editing'}
          </WorkspaceButton>
        </>
      }
    >
      <form
        className="flex flex-col gap-3.5"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div>
          <label
            htmlFor="rl-module-name"
            className="mb-1.5 block text-meta font-medium text-ink-700"
          >
            Name <span className="font-normal text-posture-critical">*</span>
          </label>
          <input
            id="rl-module-name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setRestoreError(null);
              if (create.isError) reset();
            }}
            placeholder="e.g. Fleet Management"
            autoFocus
            autoComplete="off"
            maxLength={120}
            aria-invalid={clash != null}
            aria-describedby="rl-module-name-help"
            className={FIELD}
          />
          <div id="rl-module-name-help" className="mt-1.5 text-meta">
            {clash ? (
              <span role="alert" className="text-posture-critical">
                {clash.retired
                  ? `A module named "${clash.name}" already exists and is retired. Its name stays reserved, so restore it rather than creating it again.`
                  : `A module named "${clash.name}" already exists.`}{' '}
                {clash.retired ? (
                  <button
                    type="button"
                    disabled={restore.isPending}
                    onClick={() => restoreExisting(clash.id)}
                    className={LINK}
                  >
                    {restore.isPending ? 'Restoring…' : `Restore ${clash.name}`}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      onOpenChange(false);
                      onOpenExisting(clash.id);
                    }}
                    className={LINK}
                  >
                    Open {clash.name}
                  </button>
                )}
                {restoreError ? <span className="mt-1 block">{restoreError}</span> : null}
              </span>
            ) : (
              <span className="text-ink-500">
                The number prefix is assigned automatically from the name. Requirements are numbered
                automatically too.
              </span>
            )}
          </div>
        </div>
        <div>
          <label
            htmlFor="rl-module-category"
            className="mb-1.5 block text-meta font-medium text-ink-700"
          >
            Category
          </label>
          <select
            id="rl-module-category"
            value={categoryId}
            onChange={(e) => {
              setCategoryId(e.target.value);
              setCategoryError(null);
            }}
            className={FIELD}
          >
            {categoryOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
            <option value={NEW_CATEGORY}>New category…</option>
          </select>
        </div>
        {addingCategory ? (
          <div>
            <label
              htmlFor="rl-category-name"
              className="mb-1.5 block text-meta font-medium text-ink-700"
            >
              New category name <span className="font-normal text-posture-critical">*</span>
            </label>
            <input
              id="rl-category-name"
              value={newCategoryName}
              onChange={(e) => {
                setNewCategoryName(e.target.value);
                setCategoryError(null);
              }}
              placeholder="e.g. Public Safety"
              autoComplete="off"
              maxLength={120}
              aria-invalid={categoryClash != null}
              aria-describedby="rl-category-name-help"
              className={FIELD}
            />
            <div id="rl-category-name-help" className="mt-1.5 text-meta">
              {categoryClash ? (
                <span role="alert" className="text-posture-critical">
                  {categoryClash.status === 'retired'
                    ? `A category named "${categoryClash.name}" already exists and is retired. Its name stays reserved.`
                    : `A category named "${categoryClash.name}" already exists.`}{' '}
                  {categoryClash.status === 'active' ? (
                    <button
                      type="button"
                      onClick={() => setCategoryId(categoryClash.id)}
                      className={LINK}
                    >
                      Use {categoryClash.name}
                    </button>
                  ) : null}
                </span>
              ) : (
                <span className="text-ink-500">
                  The category is created with the module and added at the end of the list.
                </span>
              )}
            </div>
          </div>
        ) : null}
        {otherError ? (
          <p role="alert" className="text-meta text-posture-critical">
            {otherError}
          </p>
        ) : null}
      </form>
    </WorkspaceDialog>
  );
}
